import { MutableRefObject, useEffect, useRef, useState, RefObject } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { isValidCoordinates } from "../utils/isValidCoordinates";
import { safeCoordTuple } from "../utils/safeCoordTuple";
import { DEFAULT_MAP_CENTER } from "../../../../constances/DefaultMapCenter.constance";
import { CommonMessage } from "../../../base/components/message/CommonMessage";

interface UseMapSetupResult {
  mapRef: RefObject<HTMLDivElement>;
  mapInst: MutableRefObject<L.Map | null>;
  gpsMarkerRef: MutableRefObject<L.Marker | null>;
  mapReady: boolean;
  coords: [number, number] | null;
  geoReady: boolean;
  geoError: string | null;
  isGeoValid: boolean;
}

export const useMapSetup = (isMountedRef: MutableRefObject<boolean>, onWarning: (msg: string) => void): UseMapSetupResult => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInst = useRef<L.Map | null>(null);
  const gpsMarkerRef = useRef<L.Marker | null>(null);

  const [mapReady, setMapReady] = useState(false);
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [geoReady, setGeoReady] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [isGeoValid, setIsGeoValid] = useState(true);
  const lastDimension = useRef({ w: 0, h: 0 });

  useEffect(() => {
    if (!mapRef.current || mapInst.current) return;
    if (!isValidCoordinates(DEFAULT_MAP_CENTER[0], DEFAULT_MAP_CENTER[1])) {
      CommonMessage.error("DEFAULT_MAP_CENTER không hợp lệ");
      return;
    }

    let watchId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;

    try {
      const map = L.map(mapRef.current, {
        center: DEFAULT_MAP_CENTER,
        zoom: 14,
        zoomControl: true,
        attributionControl: false,
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        maxZoom: 19,
      }).addTo(map);

      map.zoomControl?.setPosition("bottomleft");
      mapInst.current = map;
      if (isMountedRef.current) setMapReady(true);

      resizeObserver = new ResizeObserver((entries) => {
        if (!entries || entries.length === 0) return;

        const entry = entries[0];
        const { width, height } = entry.contentRect;

        if (Math.abs(lastDimension.current.w - width) > 1 || Math.abs(lastDimension.current.h - height) > 1) {
          lastDimension.current = { w: width, h: height };
          window.requestAnimationFrame(() => {
            if (isMountedRef.current && mapInst.current) {
              try {
                mapInst.current.invalidateSize({ animate: false, pan: true });
              } catch {}
            }
          });
        }
      });

      if (mapRef.current) resizeObserver.observe(mapRef.current);

      if (!("geolocation" in navigator)) {
        const msg = "Thiết bị không hỗ trợ GPS";
        if (isMountedRef.current) {
          onWarning(msg);
          setGeoError(msg);
          setGeoReady(false);
          setIsGeoValid(false);
        }
        return;
      }

      watchId = navigator.geolocation.watchPosition(
        ({ coords: c }) => {
          if (!isMountedRef.current) return;

          const tuple = safeCoordTuple(c.latitude, c.longitude);
          if (!tuple) {
            setGeoError("GPS trả về tọa độ không hợp lệ");
            setGeoReady(false);
            setIsGeoValid(false);
            return;
          }

          setCoords(tuple);
          setGeoReady(true);
          setGeoError(null);
          setIsGeoValid(true);

          if (!mapInst.current) return;

          try {
            const icon = L.divIcon({
              className: "gps-marker",
              html: `<div style="position:relative;width:20px;height:20px;display:flex;align-items:center;justify-content:center;">
                <div style="position:absolute;inset:0;border-radius:50%;background:rgba(37,99,235,.15);animation:_gps_pulse 2s ease-out infinite;"></div>
                <div style="width:10px;height:10px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 2px 8px rgba(37,99,235,.5);position:relative;z-index:1;"></div>
              </div>`,
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            });

            if (gpsMarkerRef.current) {
              gpsMarkerRef.current.setLatLng(tuple);
            } else {
              gpsMarkerRef.current = L.marker(tuple, { icon }).addTo(mapInst.current);
            }
          } catch (err) {
            CommonMessage.error("GPS marker error");
          }
        },
        (err) => {
          if (!isMountedRef.current) return;
          setGeoReady(false);
          setIsGeoValid(false);
          const msg = err.code === 1 ? "GPS bị từ chối. Vui lòng bật quyền định vị." : "Không lấy được vị trí. Vui lòng bật định vị.";
          setGeoError(msg);
          onWarning(msg);
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 },
      );
    } catch (err) {
      CommonMessage.error("Lỗi khởi tạo bản đồ:");
    }

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      resizeObserver?.disconnect();
      if (mapInst.current) {
        mapInst.current.remove();
        mapInst.current = null;
      }
      if (isMountedRef) setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mapRef, mapInst, gpsMarkerRef, mapReady, coords, geoReady, geoError, isGeoValid };
};
